import { handleBookingUpdateError } from '../utils/errorHandler';
import { BOOKINGS_CHANGED_EVENT, type BookingsChangedDetail } from '../services/bookingEvents';
import { useState, useEffect, useCallback, useRef } from 'react';
import { bookingService } from '../services/bookingService';
import { Booking } from '../types/booking';

interface UseBookingsProps {
  includeArchived?: boolean;
  initialPage?: number;
  initialLimit?: number;
  enabled?: boolean;
  dateFrom?: string;
  dateTo?: string;
  allPages?: boolean;
  selectedId?: string | null;
  cacheMaxAge?: number;
}

export const useBookings = ({ includeArchived = false, initialPage = 1, initialLimit = 50, enabled = true, dateFrom = '', dateTo = '', allPages = false, selectedId, cacheMaxAge = 0 }: UseBookingsProps = {}) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination State
  const [pagination, setPagination] = useState({
    page: initialPage,
    limit: initialLimit,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  });

  // Filter State
  const [filters, setFilters] = useState({
    search: '',
    source: 'all',
    status: 'all',
    dateFrom,
    dateTo,
    rechnungAngefordert: 'all'
  });

  const request = useRef(0);
  const cache = useRef<{
    key: string;
    loadedAt: number;
    response: Awaited<ReturnType<typeof bookingService.getBookings>>;
  } | null>(null);
  const fetchBookings = useCallback(async (force = false) => {
    const currentRequest = ++request.current;
    if (!enabled) { setBookings([]); setLoading(false); setError(null); return; }
    setLoading(true);
    setError(null);
    try {
      if (selectedId) {
        const booking = await bookingService.getBookingWithDetails(selectedId);
        if (currentRequest !== request.current) return;
        setBookings([booking]);
        setPagination(prev => ({ ...prev, total: 1, totalPages: 1, hasNext: false, hasPrev: false }));
        return;
      }
      const queryFilters = {
        ...filters, includeArchived, ...(allPages ? { dateFrom, dateTo } : {}), page: allPages ? 1 : pagination.page, limit: pagination.limit
      };
      const cacheKey = JSON.stringify(queryFilters);
      const cached = !force && allPages && cacheMaxAge > 0 && cache.current?.key === cacheKey
        && Date.now() - cache.current.loadedAt < cacheMaxAge ? cache.current.response : null;
      if (!cached) cache.current = null;
      let response = cached ?? await bookingService.getBookings(undefined, undefined, queryFilters);
      if (!cached && allPages && !Array.isArray(response)) {
        const combined = [...response.data];
        for (let page = 2; page <= response.pagination.totalPages; page++) {
          if (currentRequest !== request.current) return;
          const next = await bookingService.getBookings(undefined, undefined, { ...filters, includeArchived, dateFrom, dateTo, page, limit: pagination.limit });
          combined.push(...(Array.isArray(next) ? next : next.data));
        }
        response = { ...response, data: combined };
      }
      if (currentRequest !== request.current) return;

      if (!cached && allPages && cacheMaxAge > 0) {
        cache.current = { key: cacheKey, loadedAt: Date.now(), response };
      }

      if ('pagination' in response) {
        setBookings(response.data);
        setPagination(prev => ({
            ...prev,
            total: response.pagination.totalItems || response.pagination.total || 0,
            totalPages: response.pagination.totalPages || 1,
            hasNext: response.pagination.currentPage < response.pagination.totalPages,
            hasPrev: response.pagination.currentPage > 1
        }));
      } else {
        // Legacy Array Fallback - validate response is array
        if (Array.isArray(response)) {
          setBookings(response);
          setPagination(prev => ({ ...prev, total: response.length }));
        } else {
          console.error('[useBookings] Unexpected response format:', response);
          setBookings([]);
          setPagination(prev => ({ ...prev, total: 0 }));
        }
      }
    } catch (err) {
      if (currentRequest !== request.current) return;
      setBookings([]);
      setError(handleBookingUpdateError(err));
    } finally {
      if (currentRequest === request.current) setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, enabled, allPages, selectedId, dateFrom, dateTo, cacheMaxAge, includeArchived]);

  const fetchRef = useRef(fetchBookings);
  useEffect(() => { fetchRef.current = fetchBookings; }, [fetchBookings]);

  // Debounce Search Trigger (Removed)
  // The hook is now purely reactive to filter changes. Debouncing should happen in the UI component.
  useEffect(() => {
    fetchBookings();
    return () => { request.current++; };
  }, [fetchBookings]);

  useEffect(() => {
    const onChanged = (event: Event) => {
      cache.current = null;
      const id = (event as CustomEvent<BookingsChangedDetail>).detail?.enteredInIlocaId;
      if (!id) {
        // Reload instead of only discarding: a running pre-write request is superseded (request counter),
        // so an open search or list never stays empty after a write elsewhere.
        void fetchRef.current(true);
        return;
      }
      setBookings(current => current.map(booking => booking.id === id
        ? { ...booking, status: 'checked_out', bookingStatus: undefined,
          botBooking: { ...booking.botBooking, enteredInIlocaAt: new Date().toISOString() } }
        : booking));
    };
    window.addEventListener(BOOKINGS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(BOOKINGS_CHANGED_EVENT, onChanged);
  }, []);

  const setPage = (page: number) => setPagination(prev => ({ ...prev, page }));
  const setLimit = (limit: number) => setPagination(prev => ({ ...prev, limit, page: 1 })); // Reset to page 1 on limit change

  // Optimistic Update Helper
  const updateLocalBooking = (updatedBooking: Booking) => {
    setBookings(currentBookings => 
      currentBookings.map(b => 
        b.bookingReference === updatedBooking.bookingReference ? updatedBooking : b
      )
    );
  };

  const refresh = useCallback(() => fetchBookings(true), [fetchBookings]);

  return {
    bookings,
    loading,
    error,
    pagination,
    filters,
    setFilters, // Erlaubt partielles Update der Filter
    setPage,
    setLimit,
    refresh,
    updateLocalBooking // Expose helper
  };
};
